import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import type {
  Note,
  Notebook,
  NoteColor,
  NotePatch,
  NotePermission,
  NoteSummary,
} from "@websede/contracts";
import { noteColors } from "@websede/contracts";
import {
  ArrowLeft,
  BookOpen,
  Download,
  Lightbulb,
  Mail,
  Palette,
  Pencil,
  Pin,
  Plus,
  Search,
  Tag,
  Trash2,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { type MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiRequestError, api } from "../api/client.js";
import { useSession } from "../auth/session.js";
import { useEntitlements } from "../components/billing-banner.js";
import { ErrorNotice } from "../components/form.js";
import { ShellSearch, ShellSidebar, useShell } from "../components/shell-slots.js";
import { exportFileName, renderNote, toggleChecklist } from "../notes/markdown.js";

/** Keep's pastel note colours; styles.css switches them to deep tones in dark mode. */
function noteBackground(color: NoteColor): string {
  return `var(--note-${color})`;
}
const SAVE_DELAY_MS = 800;

/** Google Keep's layout: notebooks and tags on the left, notes as cards, a note opens on top. */
export function NotesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { closeDrawer } = useShell();
  const search = useSearch({ from: "/app/notes" });
  const entitlements = useEntitlements();
  const [text, setText] = useState("");
  const [query, setQuery] = useState("");
  const [newBook, setNewBook] = useState("");
  const [creatingBook, setCreatingBook] = useState(false);

  const notebooks = useQuery({
    queryKey: ["notes", "notebooks"],
    queryFn: () => api.notes.notebooks(),
  });
  const tags = useQuery({ queryKey: ["notes", "tags"], queryFn: () => api.notes.tags() });
  const filter = {
    ...(search.shared ? { shared: "1" as const } : {}),
    ...(search.notebook ? { notebookId: search.notebook } : {}),
    ...(search.tag ? { tag: search.tag } : {}),
    ...(query ? { q: query } : {}),
  };
  const list = useQuery({
    queryKey: ["notes", "list", filter],
    queryFn: () => api.notes.list(filter),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["notes"] });

  const create = useMutation({
    mutationFn: () => api.notes.create(search.notebook ? { notebookId: search.notebook } : {}),
    onSuccess: (note) => {
      closeDrawer();
      void refresh();
      void navigate({ to: "/notes", search: { ...search, note: note.id } });
    },
  });
  const createBook = useMutation({
    mutationFn: (name: string) => api.notes.createNotebook(name),
    onSuccess: (book) => {
      setNewBook("");
      setCreatingBook(false);
      closeDrawer();
      void refresh();
      void navigate({ to: "/notes", search: { notebook: book.id } });
    },
  });
  const pin = useMutation({
    mutationFn: (note: NoteSummary) => api.notes.update(note.id, { pinned: !note.pinned }),
    onSuccess: () => void refresh(),
  });

  // Debounce the search box so every keystroke does not hit the server.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(text.trim()), 250);
    return () => clearTimeout(timer);
  }, [text]);

  const heading = search.shared
    ? t("notes.sharedWithMe")
    : search.tag
      ? `#${search.tag}`
      : (notebooks.data?.notebooks.find((b) => b.id === search.notebook)?.name ?? t("notes.all"));
  const go = (next: { shared?: "1"; notebook?: string; tag?: string }) => {
    closeDrawer();
    void navigate({ to: "/notes", search: next });
  };
  const navButton = (
    key: string,
    active: boolean,
    label: string,
    Icon: typeof Lightbulb,
    onClick: () => void,
    count?: number,
  ) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      className={`flex h-10 items-center gap-4 rounded-r-full pr-3 pl-6 text-left text-sm ${active ? "bg-accent-soft font-medium text-accent" : "text-ink-2 hover:bg-ink/5"}`}
    >
      <Icon className="size-[18px] shrink-0" strokeWidth={active ? 2.2 : 1.8} />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count ? <span className="text-xs tabular-nums">{count}</span> : null}
    </button>
  );
  const notes = list.data?.notes ?? [];
  const pinned = notes.filter((n) => n.pinned);
  const others = notes.filter((n) => !n.pinned);
  const openNote = (note: NoteSummary) =>
    void navigate({ to: "/notes", search: { ...search, note: note.id } });

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      {entitlements.data && !entitlements.data.features.notes ? (
        <p className="shrink-0 border-b border-rule bg-accent-soft px-4 py-2 text-sm text-accent">
          {t("billing.notesNotInPlan")}
        </p>
      ) : null}
      <ShellSearch>
        <div className="flex h-full min-w-0 flex-1 items-center">
          <Search className="ml-4 hidden size-5 shrink-0 text-ink-2 md:block" />
          <input
            type="search"
            className="h-full min-w-0 flex-1 bg-transparent px-2 text-base outline-none placeholder:text-ink-2 md:px-3"
            placeholder={t("notes.search")}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          {text ? (
            <button
              type="button"
              aria-label={t("mail.clearSearch")}
              className="flex size-10 shrink-0 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5"
              onClick={() => setText("")}
            >
              <X className="size-5" />
            </button>
          ) : null}
        </div>
      </ShellSearch>

      <ShellSidebar>
        <div className="hidden px-2 pt-1 pb-4 md:block">
          <button
            type="button"
            disabled={create.isPending}
            onClick={() => create.mutate()}
            className="flex h-14 items-center gap-3 rounded-2xl bg-accent-soft pr-6 pl-4 text-sm font-medium text-accent hover:shadow-1"
          >
            <Pencil className="size-5" />
            {t("notes.newNote")}
          </button>
        </div>
        <nav aria-label={t("nav.notes")} className="flex flex-col pr-2 md:pr-3">
          {navButton(
            "all",
            !search.notebook && !search.tag && !search.shared,
            t("notes.all"),
            Lightbulb,
            () => go({}),
          )}
          {navButton("shared", Boolean(search.shared), t("notes.sharedWithMe"), Users, () =>
            go({ shared: "1" }),
          )}
          <div className="mt-3 mb-1 pl-6 text-xs font-medium tracking-wide text-ink-3 uppercase">
            {t("notes.notebooks")}
          </div>
          {(notebooks.data?.notebooks ?? []).map((book) =>
            navButton(
              book.id,
              search.notebook === book.id,
              book.name,
              BookOpen,
              () => go({ notebook: book.id }),
              book.noteCount,
            ),
          )}
          {creatingBook ? (
            <form
              className="py-1 pr-2 pl-6"
              onSubmit={(event) => {
                event.preventDefault();
                if (newBook.trim()) createBook.mutate(newBook.trim());
              }}
            >
              <input
                // biome-ignore lint/a11y/noAutofocus: opened on purpose to type the name
                autoFocus
                className="h-9 w-full rounded-lg border border-accent bg-surface px-3 text-sm outline-none"
                placeholder={t("notes.notebookName")}
                value={newBook}
                onChange={(e) => setNewBook(e.target.value)}
                onBlur={() => {
                  if (!newBook.trim()) setCreatingBook(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setCreatingBook(false);
                }}
              />
            </form>
          ) : (
            navButton("new-book", false, t("notes.newNotebook"), Plus, () => setCreatingBook(true))
          )}
          {(tags.data?.tags.length ?? 0) > 0 ? (
            <>
              <div className="mt-3 mb-1 pl-6 text-xs font-medium tracking-wide text-ink-3 uppercase">
                {t("notes.tags")}
              </div>
              {(tags.data?.tags ?? []).map(({ tag, count }) =>
                navButton(`tag-${tag}`, search.tag === tag, tag, Tag, () => go({ tag }), count),
              )}
            </>
          ) : null}
        </nav>
      </ShellSidebar>

      <div className="min-h-0 flex-1 overflow-auto px-3 pb-24 md:px-6 md:pb-8">
        <div className="mx-auto max-w-[600px] pt-4 pb-2">
          <button
            type="button"
            disabled={create.isPending}
            onClick={() => create.mutate()}
            className="flex h-12 w-full items-center gap-3 rounded-xl border border-rule bg-surface px-4 text-left text-base text-ink-3 shadow-1 hover:text-ink-2"
          >
            <span className="flex-1">{t("notes.takeNote")}</span>
            <Pencil className="size-5" />
          </button>
        </div>
        <h1 className="sr-only">{heading}</h1>
        {search.shared || search.tag || search.notebook ? (
          <div className="px-1 pt-4 pb-1 text-lg text-ink">{heading}</div>
        ) : null}
        <ErrorNotice error={list.error ?? create.error ?? pin.error} />
        {pinned.length > 0 ? (
          <>
            <div className="px-2 pt-4 pb-2 text-[11px] font-medium tracking-wider text-ink-3 uppercase">
              {t("notes.pinned")}
            </div>
            <NoteGrid notes={pinned} onOpen={openNote} onPin={(n) => pin.mutate(n)} />
            <div className="px-2 pt-6 pb-2 text-[11px] font-medium tracking-wider text-ink-3 uppercase">
              {t("notes.others")}
            </div>
          </>
        ) : (
          <div className="h-4" />
        )}
        <NoteGrid notes={others} onOpen={openNote} onPin={(n) => pin.mutate(n)} />
        {list.data && notes.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center text-sm text-ink-3">
            <Lightbulb className="size-16" strokeWidth={1.2} />
            {query ? t("notes.noMatch") : t("notes.empty")}
          </div>
        ) : null}
      </div>

      {search.note ? null : (
        <button
          type="button"
          aria-label={t("notes.newNote")}
          disabled={create.isPending}
          onClick={() => create.mutate()}
          className="absolute right-4 bottom-4 flex size-14 items-center justify-center rounded-2xl bg-accent-soft text-accent shadow-2 md:hidden"
        >
          <Plus className="size-6" />
        </button>
      )}

      {search.note ? (
        <NoteEditor
          key={search.note}
          id={search.note}
          notebooks={notebooks.data?.notebooks ?? []}
          onChanged={() => void refresh()}
          onClose={() => {
            const { note: _note, ...rest } = search;
            void navigate({ to: "/notes", search: rest });
          }}
        />
      ) : null}
    </div>
  );
}

function NoteGrid({
  notes,
  onOpen,
  onPin,
}: {
  notes: NoteSummary[];
  onOpen: (note: NoteSummary) => void;
  onPin: (note: NoteSummary) => void;
}) {
  return (
    <ul className="columns-2 gap-3 sm:columns-3 lg:columns-4 xl:columns-5">
      {notes.map((note) => (
        <NoteCard key={note.id} note={note} onOpen={() => onOpen(note)} onPin={() => onPin(note)} />
      ))}
    </ul>
  );
}

/** A Keep card: tinted by its colour, title, a few lines of text, tags. */
function NoteCard({
  note,
  onOpen,
  onPin,
}: {
  note: NoteSummary;
  onOpen: () => void;
  onPin: () => void;
}) {
  const { t } = useTranslation();
  return (
    <li
      className="group relative mb-3 break-inside-avoid rounded-xl border border-rule hover:shadow-1"
      style={
        note.color
          ? { background: noteBackground(note.color), borderColor: "transparent" }
          : undefined
      }
    >
      <button type="button" onClick={onOpen} className="block w-full px-4 pt-3 pb-3 text-left">
        {note.title ? (
          <span className="block pr-6 text-base leading-snug font-medium break-words text-ink">
            {note.title}
          </span>
        ) : null}
        {note.preview ? (
          <span className="mt-1.5 line-clamp-[10] block text-sm leading-relaxed break-words whitespace-pre-line text-ink-2">
            {note.preview}
          </span>
        ) : !note.title ? (
          <span className="block text-sm text-ink-3">{t("notes.emptyNote")}</span>
        ) : null}
        {note.tags.length > 0 || note.shared || note.owner ? (
          <span className="mt-3 flex flex-wrap gap-1">
            {note.owner || note.shared ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-ink/5 px-2 py-0.5 text-[11px] text-ink-2">
                <Users className="size-3" />
                {note.owner ? note.owner.displayName : t("notes.share")}
              </span>
            ) : null}
            {note.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-ink/5 px-2 py-0.5 text-[11px] text-ink-2">
                {tag}
              </span>
            ))}
          </span>
        ) : null}
      </button>
      {note.access === "owner" ? (
        <button
          type="button"
          aria-label={note.pinned ? t("notes.unpin") : t("notes.pin")}
          aria-pressed={note.pinned}
          onClick={onPin}
          className={`absolute top-1.5 right-1.5 flex size-8 items-center justify-center rounded-full text-ink-2 hover:bg-ink/10 ${note.pinned ? "" : "opacity-0 group-hover:opacity-100 focus:opacity-100"}`}
        >
          <Pin className={`size-4 ${note.pinned ? "fill-current" : ""}`} />
        </button>
      ) : null}
    </li>
  );
}

function NoteEditor({
  id,
  notebooks,
  onChanged,
  onClose,
}: {
  id: string;
  notebooks: Notebook[];
  onChanged: () => void;
  onClose: () => void;
}) {
  const note = useQuery({
    queryKey: ["notes", "note", id],
    queryFn: () => api.notes.get(id),
    // Shared notes pick up what colleagues saved; private ones only change here.
    refetchInterval: (query) => {
      const data = query.state.data;
      return data && (data.access !== "owner" || data.shares.length > 0) ? 15_000 : false;
    },
  });
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!note.data) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6">
        <div className="w-full max-w-md rounded-2xl bg-surface p-6 shadow-2">
          <ErrorNotice error={note.error} />
        </div>
      </div>
    );
  }
  return (
    <LoadedEditor note={note.data} notebooks={notebooks} onChanged={onChanged} onClose={onClose} />
  );
}

type SaveStatus = "saved" | "dirty" | "saving" | "conflict" | "error";

function LoadedEditor({
  note,
  notebooks,
  onChanged,
  onClose,
}: {
  note: Note;
  notebooks: Notebook[];
  onChanged: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const session = useSession();
  const isOwner = note.access === "owner";
  const canWrite = note.access !== "view";
  const [title, setTitleState] = useState(note.title);
  const [body, setBodyState] = useState(note.body);
  const [tagText, setTagText] = useState(note.tags.join(", "));
  const [editing, setEditing] = useState(canWrite && note.body.trim() === "");
  const [status, setStatus] = useState<SaveStatus>("saved");
  const [sharing, setSharing] = useState(false);
  const [showPalette, setShowPalette] = useState(false);
  /** The text as last saved, and the server version it was saved on top of. */
  const saved = useRef({ title: note.title, body: note.body });
  const base = useRef(note.updatedAt);
  const latest = useRef({ title, body });
  latest.current = { title, body };
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const setTitle = (value: string) => {
    setTitleState(value);
    setStatus((s) => (s === "error" ? "dirty" : s));
  };
  const setBody = (update: (current: string) => string) => {
    setBodyState(update);
    setStatus((s) => (s === "error" ? "dirty" : s));
  };

  const accept = (updated: Note) => {
    base.current = updated.updatedAt;
    queryClient.setQueryData(["notes", "note", updated.id], updated);
    onChangedRef.current();
  };
  const save = useMutation({
    mutationFn: (patch: NotePatch) => api.notes.update(note.id, patch),
    onSuccess: accept,
  });
  const saveNow = save.mutate;

  // Autosave the text a moment after typing stops. The save names the version it builds on, so
  // a colleague's newer text is never overwritten silently.
  useEffect(() => {
    if (!canWrite || status === "conflict" || status === "error" || status === "saving") return;
    if (title === saved.current.title && body === saved.current.body) return;
    setStatus("dirty");
    const timer = setTimeout(() => {
      const patch: NotePatch = { ifUpdatedAt: base.current };
      if (title !== saved.current.title) patch.title = title;
      if (body !== saved.current.body) patch.body = body;
      const previous = saved.current;
      saved.current = { title, body };
      setStatus("saving");
      saveNow(patch, {
        onSuccess: () => setStatus("saved"),
        onError: (error) => {
          saved.current = previous;
          setStatus(
            error instanceof ApiRequestError && error.status === 409 ? "conflict" : "error",
          );
        },
      });
    }, SAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [title, body, saveNow, canWrite, status]);

  // A newer version from the server replaces the text, unless there are unsaved words here.
  useEffect(() => {
    if (note.updatedAt === base.current) return;
    const clean =
      latest.current.title === saved.current.title && latest.current.body === saved.current.body;
    if (!clean) return;
    base.current = note.updatedAt;
    saved.current = { title: note.title, body: note.body };
    setTitleState(note.title);
    setBodyState(note.body);
  }, [note.updatedAt, note.title, note.body]);

  // Anything still unsaved is sent when the note closes.
  useEffect(
    () => () => {
      const { title: lastTitle, body: lastBody } = latest.current;
      if (lastTitle !== saved.current.title || lastBody !== saved.current.body) {
        void api.notes
          .update(note.id, { title: lastTitle, body: lastBody, ifUpdatedAt: base.current })
          .then(() => onChangedRef.current())
          .catch(() => undefined);
      }
    },
    [note.id],
  );

  const loadTheirs = async () => {
    const fresh = await api.notes.get(note.id);
    base.current = fresh.updatedAt;
    saved.current = { title: fresh.title, body: fresh.body };
    setTitleState(fresh.title);
    setBodyState(fresh.body);
    queryClient.setQueryData(["notes", "note", fresh.id], fresh);
    setStatus("saved");
  };
  const keepMine = () => {
    const mine = { title: latest.current.title, body: latest.current.body };
    setStatus("saving");
    saveNow(mine, {
      onSuccess: () => {
        saved.current = mine;
        setStatus("saved");
      },
      onError: () => setStatus("error"),
    });
  };

  const leave = useMutation({
    mutationFn: () => api.notes.unshare(note.id, session.data?.user.id ?? ""),
    onSuccess: () => {
      onChangedRef.current();
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.notes.remove(note.id),
    onSuccess: () => {
      onChangedRef.current();
      onClose();
    },
  });

  const html = useMemo(() => renderNote(body), [body]);
  const onPreviewClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target instanceof HTMLInputElement && target.type === "checkbox") {
      if (!canWrite) {
        event.preventDefault();
        return;
      }
      const boxes = [...event.currentTarget.querySelectorAll('input[type="checkbox"]')];
      setBody((current) => toggleChecklist(current, boxes.indexOf(target)));
      return;
    }
    const anchor = target.closest("a");
    if (anchor?.href) {
      event.preventDefault();
      window.open(anchor.href, "_blank", "noopener,noreferrer");
    }
  };

  const exportMarkdown = () => {
    const blob = new Blob([`# ${title || t("notes.untitled")}\n\n${body}\n`], {
      type: "text/markdown;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = exportFileName(title);
    link.click();
    URL.revokeObjectURL(url);
  };

  const statusText =
    status === "saved"
      ? t("common.saved")
      : status === "error"
        ? t("notes.notSaved")
        : status === "conflict"
          ? t("notes.conflictShort")
          : t("notes.saving");
  const iconButton =
    "flex size-10 shrink-0 items-center justify-center rounded-full text-ink-2 hover:bg-ink/10 disabled:opacity-40";

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center md:items-center md:bg-black/40 md:p-6">
      <button
        type="button"
        aria-label={t("common.close")}
        className="absolute inset-0 hidden cursor-default md:block"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-label={title || t("notes.untitled")}
        className="relative flex w-full flex-col overflow-hidden bg-surface md:max-h-[88vh] md:max-w-[680px] md:rounded-2xl md:shadow-2"
        style={note.color ? { background: noteBackground(note.color) } : undefined}
      >
        <div className="flex h-14 shrink-0 items-center gap-1 px-2">
          <button
            type="button"
            aria-label={t("mail.back")}
            className={`${iconButton} md:hidden`}
            onClick={onClose}
          >
            <ArrowLeft className="size-5" />
          </button>
          <span className="flex-1 truncate px-2 text-xs text-ink-3">
            {canWrite ? statusText : ""}
          </span>
          {isOwner ? (
            <button
              type="button"
              aria-label={note.pinned ? t("notes.unpin") : t("notes.pin")}
              aria-pressed={note.pinned}
              title={note.pinned ? t("notes.unpin") : t("notes.pin")}
              className={iconButton}
              onClick={() => save.mutate({ pinned: !note.pinned })}
            >
              <Pin className={`size-5 ${note.pinned ? "fill-current text-ink" : ""}`} />
            </button>
          ) : null}
          <button
            type="button"
            aria-label={t("common.close")}
            className={`${iconButton} hidden md:flex`}
            onClick={onClose}
          >
            <X className="size-5" />
          </button>
        </div>

        {isOwner ? null : (
          <div className="mx-4 mb-2 flex flex-wrap items-center gap-2 rounded-lg bg-ink/5 px-3 py-2 text-sm text-ink-2">
            <Users className="size-4" />
            <span className="flex-1">
              {t("notes.sharedBy", { name: note.owner?.displayName ?? "" })} ·{" "}
              {note.access === "edit" ? t("notes.canEdit") : t("notes.canView")}
            </span>
            <button
              type="button"
              disabled={leave.isPending}
              className="rounded-full px-3 py-1 text-sm font-medium text-accent hover:bg-accent-soft"
              onClick={() => leave.mutate()}
            >
              {t("notes.leave")}
            </button>
          </div>
        )}
        {status === "conflict" ? (
          <div
            className="mx-4 mb-2 flex flex-wrap items-center gap-2 rounded-lg bg-warn-soft px-3 py-2 text-sm text-warn"
            role="alert"
          >
            <span className="flex-1">{t("notes.conflict")}</span>
            <button
              type="button"
              className="rounded-full border border-warn/40 px-3 py-1 text-sm"
              onClick={() => void loadTheirs()}
            >
              {t("notes.loadTheirs")}
            </button>
            <button
              type="button"
              className="rounded-full bg-accent px-3 py-1 text-sm font-medium text-white"
              onClick={keepMine}
            >
              {t("notes.keepMine")}
            </button>
          </div>
        ) : null}
        {sharing && isOwner ? <SharePanel note={note} /> : null}

        <div className="flex min-h-0 flex-1 flex-col overflow-auto px-5 pb-3">
          <input
            className="w-full bg-transparent py-1 text-[22px] leading-snug text-ink outline-none placeholder:text-ink-3"
            placeholder={t("notes.title")}
            value={title}
            readOnly={!canWrite}
            onChange={(e) => setTitle(e.target.value)}
            aria-label={t("notes.title")}
          />
          {canWrite ? (
            <div className="mt-2 flex items-center gap-1 text-xs">
              <button
                type="button"
                aria-pressed={editing}
                className={`rounded-full px-3 py-1 ${editing ? "bg-ink/10 font-medium text-ink" : "text-ink-3 hover:bg-ink/5"}`}
                onClick={() => setEditing(true)}
              >
                {t("notes.edit")}
              </button>
              <button
                type="button"
                aria-pressed={!editing}
                className={`rounded-full px-3 py-1 ${editing ? "text-ink-3 hover:bg-ink/5" : "bg-ink/10 font-medium text-ink"}`}
                onClick={() => setEditing(false)}
              >
                {t("notes.preview")}
              </button>
              <span className="hidden truncate pl-2 text-ink-3 sm:inline">
                {t("notes.markdownHint")}
              </span>
            </div>
          ) : null}
          {editing && canWrite ? (
            <textarea
              // biome-ignore lint/a11y/noAutofocus: a new, empty note is opened to be written
              autoFocus={note.body.trim() === "" && note.title === ""}
              aria-label={t("notes.body")}
              placeholder={t("notes.takeNote")}
              className="mt-2 min-h-64 flex-1 resize-none bg-transparent text-[15px] leading-relaxed text-ink outline-none placeholder:text-ink-3"
              value={body}
              onChange={(e) => {
                const value = e.target.value;
                setBody(() => value);
              }}
            />
          ) : (
            <div
              className="note-prose mt-2 min-h-40 text-[15px] text-ink"
              onClick={onPreviewClick}
              onKeyDown={() => undefined}
              role="document"
              // The HTML is produced by renderNote, which sanitises it with DOMPurify.
              // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitised in renderNote
              dangerouslySetInnerHTML={{ __html: html }}
            />
          )}
          <div className="mt-4 flex flex-col gap-2">
            {isOwner ? (
              <label className="flex items-center gap-2 text-sm text-ink-2">
                <Tag className="size-4 shrink-0 text-ink-3" />
                <input
                  className="min-w-0 flex-1 bg-transparent py-1 text-sm outline-none placeholder:text-ink-3"
                  placeholder={t("notes.tagsPlaceholder")}
                  value={tagText}
                  onChange={(e) => setTagText(e.target.value)}
                  onBlur={() => {
                    const next = tagText
                      .split(",")
                      .map((tag) => tag.trim().replace(/^#/, "").toLowerCase())
                      .filter(Boolean);
                    if (next.join(",") !== note.tags.join(",")) save.mutate({ tags: next });
                  }}
                />
              </label>
            ) : note.tags.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {note.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-ink/5 px-2 py-0.5 text-xs text-ink-2">
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}
            {note.email ? (
              <Link
                to="/mail"
                search={{ email: note.email.id }}
                className="flex items-center gap-2 text-sm text-accent hover:underline"
              >
                <Mail className="size-4 shrink-0" />
                <span className="truncate">
                  {t("tasks.openEmail", { subject: note.email.subject || t("tasks.fromEmail") })}
                </span>
              </Link>
            ) : null}
          </div>
          <ErrorNotice error={save.error ?? remove.error ?? leave.error} />
        </div>

        {showPalette && isOwner ? (
          <fieldset
            className="m-0 flex flex-wrap items-center gap-2 border-0 border-t border-rule/60 px-4 py-2"
            aria-label={t("notes.color")}
          >
            <button
              type="button"
              aria-label={t("notes.noColor")}
              aria-pressed={note.color === null}
              title={t("notes.noColor")}
              className={`flex size-8 items-center justify-center rounded-full border-2 bg-surface ${note.color === null ? "border-accent" : "border-rule"}`}
              onClick={() => save.mutate({ color: null })}
            >
              <X className="size-4 text-ink-3" />
            </button>
            {noteColors.map((color) => (
              <button
                key={color}
                type="button"
                aria-label={t(`notes.colors.${color}`)}
                aria-pressed={note.color === color}
                title={t(`notes.colors.${color}`)}
                className={`size-8 rounded-full border-2 ${note.color === color ? "border-accent" : "border-transparent"}`}
                style={{ background: noteBackground(color) }}
                onClick={() => save.mutate({ color })}
              />
            ))}
          </fieldset>
        ) : null}

        <div className="flex shrink-0 items-center gap-0.5 border-t border-rule/60 px-2 py-1.5">
          {isOwner ? (
            <>
              <button
                type="button"
                aria-label={t("notes.color")}
                aria-expanded={showPalette}
                title={t("notes.color")}
                className={iconButton}
                onClick={() => setShowPalette((v) => !v)}
              >
                <Palette className="size-5" />
              </button>
              <button
                type="button"
                aria-label={t("notes.share")}
                aria-expanded={sharing}
                title={
                  note.shares.length > 0
                    ? t("notes.sharedWith", { count: note.shares.length })
                    : t("notes.share")
                }
                className={`${iconButton} ${note.shares.length > 0 ? "text-accent" : ""}`}
                onClick={() => setSharing((v) => !v)}
              >
                <UserPlus className="size-5" />
              </button>
              <label className="flex h-10 min-w-0 items-center gap-1.5 rounded-full px-2 text-sm text-ink-2 hover:bg-ink/10">
                <BookOpen className="size-5 shrink-0" />
                <select
                  className="max-w-36 min-w-0 cursor-pointer bg-transparent text-sm outline-none"
                  value={note.notebookId ?? ""}
                  aria-label={t("notes.notebook")}
                  onChange={(e) => save.mutate({ notebookId: e.target.value || null })}
                >
                  <option value="">{t("notes.noNotebook")}</option>
                  {notebooks.map((book) => (
                    <option key={book.id} value={book.id}>
                      {book.name}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : null}
          <button
            type="button"
            aria-label={t("notes.export")}
            title={t("notes.export")}
            className={iconButton}
            onClick={exportMarkdown}
          >
            <Download className="size-5" />
          </button>
          {isOwner ? (
            <button
              type="button"
              aria-label={t("notes.delete")}
              title={t("notes.delete")}
              disabled={remove.isPending}
              className={`${iconButton} hover:text-danger`}
              onClick={() => {
                if (window.confirm(t("notes.confirmDelete"))) remove.mutate();
              }}
            >
              <Trash2 className="size-5" />
            </button>
          ) : null}
          <span className="flex-1" />
          <button
            type="button"
            className="hidden h-9 rounded-full px-5 text-sm font-medium text-ink hover:bg-ink/10 md:block"
            onClick={onClose}
          >
            {t("common.close")}
          </button>
        </div>
      </div>
    </div>
  );
}

/** The owner's view of who else can open the note, with a colleague search to add more. */
function SharePanel({ note }: { note: Note }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [text, setText] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setQuery(text.trim()), 200);
    return () => clearTimeout(timer);
  }, [text]);
  const people = useQuery({
    queryKey: ["people", query],
    queryFn: () => api.people(query),
    enabled: query.length > 0,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["notes"] });
  const share = useMutation({
    mutationFn: ({ userId, permission }: { userId: string; permission: NotePermission }) =>
      api.notes.share(note.id, userId, permission),
    onSuccess: () => {
      setText("");
      void refresh();
    },
  });
  const unshare = useMutation({
    mutationFn: (userId: string) => api.notes.unshare(note.id, userId),
    onSuccess: () => void refresh(),
  });
  const already = new Set(note.shares.map((s) => s.userId));
  const candidates = (people.data?.people ?? []).filter((p) => !already.has(p.id));

  return (
    <section className="mx-4 mb-2 flex flex-col gap-2 rounded-xl bg-surface px-4 py-3 text-sm shadow-1">
      <h2 className="text-sm font-semibold">{t("notes.shareTitle")}</h2>
      <ul className="flex flex-col gap-1">
        {note.shares.map((person) => (
          <li key={person.userId} className="flex flex-wrap items-center gap-2">
            <span className="min-w-0 flex-1 truncate">
              {person.displayName} <span className="text-xs text-ink-3">{person.email}</span>
            </span>
            <select
              className="rounded-md border border-rule bg-surface px-2 py-1 text-xs"
              value={person.permission}
              aria-label={t("notes.permission", { name: person.displayName })}
              onChange={(e) =>
                share.mutate({
                  userId: person.userId,
                  permission: e.target.value as NotePermission,
                })
              }
            >
              <option value="edit">{t("notes.canEdit")}</option>
              <option value="view">{t("notes.canView")}</option>
            </select>
            <button
              type="button"
              className="text-xs text-danger underline-offset-2 hover:underline"
              onClick={() => unshare.mutate(person.userId)}
            >
              {t("notes.remove")}
            </button>
          </li>
        ))}
      </ul>
      <input
        type="search"
        className="w-full rounded-md border border-rule bg-surface px-2 py-1.5 text-sm outline-none focus:border-accent"
        placeholder={t("notes.searchPeople")}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {query && people.data ? (
        candidates.length > 0 ? (
          <ul className="flex flex-col gap-1">
            {candidates.map((person) => (
              <li key={person.id} className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate">
                  {person.displayName} <span className="text-xs text-ink-3">{person.email}</span>
                </span>
                <button
                  type="button"
                  disabled={share.isPending}
                  className="rounded-full border border-rule px-3 py-1 text-sm text-accent hover:bg-accent-soft"
                  onClick={() => share.mutate({ userId: person.id, permission: "edit" })}
                >
                  {t("notes.add")}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-ink-3">{t("notes.noPeople")}</p>
        )
      ) : null}
      <ErrorNotice error={share.error ?? unshare.error} />
    </section>
  );
}
